import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from "react-router-dom";

import SettingsPage from '../partials/SettingsPage';
import LoadingSpinner from '../partials/LoadingSpinner';
import PositionGrid from '../partials/PositionGrid';

import {
  Card, BlockStack, InlineStack, Text, Button, Select, IndexTable,
  Badge, Banner, Modal, Box,
} from '@shopify/polaris';
import { RefreshIcon } from '@shopify/polaris-icons';

import { batchesIndex, batchReturnToStock, batchSetAside } from '../../redux/actions/batches';
import { UI_COLORS } from '../../constants';

const formatTime = (value) => value ? new Date(value).toLocaleString() : '-';

const toteLabel = (entry) => entry.position_label || `Tote ${(entry.tote_id || '').slice(-8)}`;

// The full return lifecycle - every status a batch can sit in once packing
// left items on the cart
const RETURN_STATUSES = ['packed_with_returns', 'to_return', 'returning', 'returning_parked', 'complete'];

const STATUS_LABELS = {
  packed_with_returns: 'Packed - Returns',
  to_return: 'To Return',
  returning: 'Returning',
  returning_parked: 'Returning (Parked)',
  complete: 'Complete'
};

const STATUS_BADGE_TONES = {
  packed_with_returns: 'warning',
  to_return: 'info',
  returning: 'info',
  returning_parked: 'warning',
  complete: 'success'
};

const ACTION_CONFIG = {
  return_to_stock: {
    header: 'Return to Stock',
    buttonText: 'Return to Stock',
    message: 'Mark this batch\'s remaining items for return to stock? The batch will move to "To return".',
    run: (props, batchId) => props.returnToStock(batchId),
  },
  set_aside: {
    header: 'Set Aside',
    buttonText: 'Set Aside',
    message: 'Set aside this batch\'s remaining items? The batch will be marked complete.',
    run: (props, batchId) => props.setAside(batchId),
  },
};

const Returns = (props) => {
  const { batches, loadBatches } = props;
  const [statusFilter, setStatusFilter] = useState('packed_with_returns');
  // { action: 'return_to_stock' | 'set_aside', batch } while a
  // confirmation is open
  const [confirm, setConfirm] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionError, setActionError] = useState(null);

  useEffect(() => {
    loadBatches(statusFilter);
  }, [statusFilter, loadBatches]);

  const openConfirm = (action, batch) => {
    setActionError(null);
    setConfirm({ action, batch });
  };

  const closeConfirm = () => {
    if (isSubmitting) return;
    setConfirm(null);
  };

  const submitAction = () => {
    if (!confirm || isSubmitting) return;

    setIsSubmitting(true);
    setActionError(null);

    ACTION_CONFIG[confirm.action].run(props, confirm.batch.id)
      .then(() => {
        setConfirm(null);
        loadBatches(statusFilter);
      })
      .catch((error) => {
        setActionError(error.message || 'The batch could not be updated.');
      })
      .finally(() => setIsSubmitting(false));
  };

  const returnsFor = (batch) => batch.return_items || [];

  const remainingItemCount = (batch) =>
    returnsFor(batch).reduce((sum, entry) => sum + (entry.items || []).reduce((s, item) => s + (item.quantity || 0), 0), 0);

  const returnedItemCount = (batch) =>
    ((batch.returning_state || {}).returned_item_ids || []).length;

  const confirmConfig = confirm ? ACTION_CONFIG[confirm.action] : null;

  return (
    <SettingsPage
      title="Completed Batches"
      description="Have questions or need some help? Contact us at support@athousandapps.com"
    >
      <Card>
        <BlockStack gap="300">
          <Text as="h2" variant="headingMd">Completed Batches</Text>
          <Text as="p" variant="bodyMd">Batches that finished packing but still have picked items on the cart. Send them to return to stock, or set the items aside.</Text>

          <InlineStack gap="300" blockAlign="center">
            <div style={{ minWidth: '220px' }}>
              <Select
                label="Status"
                labelHidden
                options={RETURN_STATUSES.map((status) => ({
                  label: STATUS_LABELS[status],
                  value: status
                }))}
                value={statusFilter}
                onChange={setStatusFilter}
              />
            </div>
            <Button
              variant="plain"
              icon={RefreshIcon}
              loading={batches.isFetching}
              onClick={() => loadBatches(statusFilter)}
            >
              Refresh
            </Button>
          </InlineStack>
        </BlockStack>
      </Card>

      { actionError &&
        <Banner
          title="Return action failed"
          tone="critical"
          onDismiss={() => setActionError(null)}
        >
          <p>{actionError}</p>
        </Banner>
      }

      { batches.errors &&
        <Banner
          title="Batch request failed"
          tone="critical"
        >
          <p>{batches.errors}</p>
        </Banner>
      }

      { batches.isFetching &&
        <Card><LoadingSpinner size="small" statusText="Loading batches"/></Card>
      }

      { batches.status === 'success' && !batches.isFetching && batches.batches.length === 0 &&
        <Banner tone="warning">
          <p>No {STATUS_LABELS[statusFilter].toLowerCase()} batches found.</p>
        </Banner>
      }

      { batches.status === 'success' && batches.batches.map((batch) => (
        <Card key={batch.id}>
          <BlockStack gap="400">
            <InlineStack align="space-between" blockAlign="center" wrap>
              <BlockStack gap="100">
                <Text as="h3" variant="headingMd"><Link to={`/batches/${batch.id}`}>{batch.name || batch.id}</Link></Text>
                <Badge tone={STATUS_BADGE_TONES[batch.status] || 'info'}>{STATUS_LABELS[batch.status] || batch.status}</Badge>
                <Text as="p">Packed: {formatTime(batch.packed_with_returns_at || batch.packed_at)}</Text>
                <Text as="p">{returnsFor(batch).length} tote{returnsFor(batch).length === 1 ? '' : 's'} holding {remainingItemCount(batch)} remaining unit{remainingItemCount(batch) === 1 ? '' : 's'}</Text>
                {batch.cart_id && <Text as="p">Cart: {batch.cart_id}</Text>}
                {batch.returner_email && <Text as="p">Returner: {batch.returner_email}</Text>}
                {batch.status === 'returning_parked' && batch.returning_state &&
                  <Text as="p">Parked by {(batch.returning_state.returner_email || 'unknown')} - {returnedItemCount(batch)} of {(batch.return_items || []).reduce((sum, entry) => sum + (entry.items || []).length, 0)} items returned</Text>
                }
                {batch.status === 'complete' && batch.returned_at &&
                  <Text as="p">Returned to stock: {formatTime(batch.returned_at)}</Text>
                }
                {batch.status === 'complete' && batch.set_aside_at &&
                  <Text as="p">Set aside: {formatTime(batch.set_aside_at)}</Text>
                }
              </BlockStack>
              { batch.status === 'packed_with_returns' &&
                <InlineStack gap="300">
                  <Button onClick={() => openConfirm('return_to_stock', batch)}>Return to Stock</Button>
                  <Button onClick={() => openConfirm('set_aside', batch)}>Set Aside</Button>
                </InlineStack>
              }
            </InlineStack>

            {returnsFor(batch).map((entry) => (
              <Box
                key={entry.tote_id}
                background="bg-surface-secondary"
                borderColor="border"
                borderWidth="025"
                borderRadius="200"
                padding="300"
              >
                <InlineStack gap="300" blockAlign="center">
                  <Text as="h4" variant="headingSm">{toteLabel(entry)}</Text>
                  <PositionGrid rows={batch.total_rows} columns={batch.total_columns} row={entry.row} column={entry.column} color={UI_COLORS.accent} />
                </InlineStack>
                <Text as="p">Shipment #{entry.order_shipment_id}</Text>
                <IndexTable
                  resourceName={{ singular: 'item', plural: 'items' }}
                  itemCount={(entry.items || []).length}
                  selectable={false}
                  headings={[
                    { title: 'Item' },
                    { title: 'SKU' },
                    { title: 'Qty' },
                  ]}
                >
                  {(entry.items || []).map((item, index) => (
                    <IndexTable.Row id={`${entry.tote_id}-${index}`} key={index} position={index}>
                      <IndexTable.Cell>{item.name}</IndexTable.Cell>
                      <IndexTable.Cell>{item.sku || '-'}</IndexTable.Cell>
                      <IndexTable.Cell>{item.quantity}</IndexTable.Cell>
                    </IndexTable.Row>
                  ))}
                </IndexTable>
              </Box>
            ))}
          </BlockStack>
        </Card>
      ))}

      <Modal
        open={confirm !== null}
        onClose={() => closeConfirm()}
        title={confirmConfig ? confirmConfig.header : ''}
        primaryAction={{
          content: isSubmitting ? 'Working...' : (confirmConfig ? confirmConfig.buttonText : ''),
          onAction: submitAction,
          disabled: isSubmitting
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => closeConfirm() },
        ]}
      >
        <Modal.Section>
          {confirm && (
            <BlockStack gap="300">
              <Text as="h3" variant="headingSm">{confirm.batch.name || confirm.batch.id}</Text>
              <Text as="p">{confirmConfig.message}</Text>
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>
    </SettingsPage>
  )
}

const mapStateToProps = state => ({
  batches: state.batches,
});

const mapDispatchToProps = (dispatch) => ({
  loadBatches: (status) => dispatch(batchesIndex(status)),
  returnToStock: (batchId) => dispatch(batchReturnToStock(batchId)),
  setAside: (batchId) => dispatch(batchSetAside(batchId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Returns);
